import { useCallback, useEffect, useState } from 'react';
import { api, AdminContribution, AdminDispute, IssuedCode, ResetRequest } from '../api';
import { Icon } from '../Icon';
import { useSession } from '../auth/session';
import { AdminReading } from './AdminReading';

/**
 * The administrator's queues: password resets, reported answer keys, and
 * submitted questions.
 *
 * All three already existed as endpoints and none had a way in, which in
 * practice meant they were never worked: a reset request sat in a table nobody
 * looked at, and a learner who reported a wrong answer key heard nothing back.
 *
 * This screen only hides the entrance. Every request below is refused by the
 * server without an editor or administrator session, so a learner who guesses
 * the route gets a 403 rather than a queue.
 */

/** Roles this screen is for. Mirrors the server's list; the server decides. */
export const ADMIN_ROLES = ['editor', 'admin'];

function when(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium', timeStyle: 'short',
  });
}

/**
 * A code, shown exactly once.
 *
 * It is deliberately not stored in component state beyond this render, not put
 * in the URL, and not written to the console: only its hash exists on the
 * server, so what is on this screen is the only copy in the world.
 */
function IssuedCodeBox({ issued, email }: { issued: IssuedCode; email: string | null }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="notice" role="status" style={{ marginTop: '0.75rem' }}>
      <p style={{ margin: '0 0 0.5rem' }}>
        <strong>Code for {email ?? 'this account'}</strong>
      </p>
      <p className="reset-code">{issued.code}</p>
      <div className="btn-row" style={{ marginBottom: '0.5rem' }}>
        <button type="button" onClick={() => {
          void navigator.clipboard?.writeText(issued.code).then(() => setCopied(true)).catch(() => {});
        }}>
          Copy code
          <span className="visually-hidden"> for {email ?? 'this account'}</span>
        </button>
        <span className="muted" aria-live="polite">{copied ? 'Copied.' : ''}</span>
      </div>
      <p className="muted" style={{ margin: 0 }}>
        Shown once and never again — the server keeps only a hash of it. Pass it to the
        person through a channel you already trust that it is them, not by replying to
        whatever asked for the reset. It expires at {when(issued.expiresAt)}.
      </p>
    </div>
  );
}

export function Admin({ onBack }: { onBack: () => void }) {
  const { learner } = useSession();
  const [resets, setResets] = useState<ResetRequest[]>([]);
  const [disputes, setDisputes] = useState<AdminDispute[]>([]);
  const [contributions, setContributions] = useState<AdminContribution[]>([]);
  const [issued, setIssued] = useState<Record<string, IssuedCode>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  /*
   * The three queues load independently. One failing endpoint must not blank
   * the other two — an administrator with a reset to issue should not be
   * stopped by an unrelated fault in the contributions query.
   */
  const load = useCallback(async () => {
    setLoading(true);
    const settled = await Promise.allSettled([
      api.get<ResetRequest[]>('/admin/reset-requests'),
      api.get<AdminDispute[]>('/admin/disputes'),
      api.get<AdminContribution[]>('/admin/contributions'),
    ]);
    const [r, d, c] = settled;
    if (r.status === 'fulfilled') setResets(r.value);
    if (d.status === 'fulfilled') setDisputes(d.value);
    if (c.status === 'fulfilled') setContributions(c.value);
    const failed = settled.filter((s) => s.status === 'rejected') as PromiseRejectedResult[];
    setErr(failed.length ? failed.map((f) => f.reason?.message).join(' ') : null);
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function act<T>(key: string, run: () => Promise<T>, done: (out: T) => string) {
    setBusy(key); setErr(null); setStatus(null);
    try {
      setStatus(done(await run()));
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(null);
    }
  }

  async function issue(req: ResetRequest) {
    await act(req.id,
      () => api.post<IssuedCode>(`/admin/reset-requests/${req.id}/issue`),
      (code) => {
        setIssued((cur) => ({ ...cur, [req.id]: code }));
        return `A reset code was issued for ${req.email}. It is shown below, once.`;
      });
    await load();
  }

  async function resolveDispute(d: AdminDispute, accept: boolean) {
    const note = (notes[d.id] ?? '').trim();
    if (!accept && !note) {
      setErr('Say why the report was not accepted — the person who raised it sees this note.');
      return;
    }
    await act(d.id,
      () => api.post(`/admin/disputes/${d.id}/resolve`, { accept, note }),
      () => accept
        ? 'Report accepted. The reporter has been paid a cookie. The key itself changes only when a corrected bank version is published.'
        : 'Report rejected, with your note.');
    await load();
  }

  async function reviewContribution(c: AdminContribution, accept: boolean) {
    const note = (notes[c.id] ?? '').trim();
    if (!accept && !note) {
      setErr('Say why the question was not accepted — the author sees this note.');
      return;
    }
    await act(c.id,
      () => api.post(`/admin/contributions/${c.id}/review`, { accept, note }),
      () => accept
        ? 'Question accepted and the author paid. It is queued for a future bank version — accepting does not publish it.'
        : 'Question rejected, with your note.');
    await load();
  }

  const noteField = (id: string, label: string) => (
    <>
      <label htmlFor={`note-${id}`} className="muted">{label}</label>
      <textarea id={`note-${id}`} rows={2} value={notes[id] ?? ''}
                onChange={(e) => setNotes((cur) => ({ ...cur, [id]: e.target.value }))}
                className="admin-note" />
    </>
  );

  return (
    <>
      <button className="btn-ghost btn-back" onClick={onBack}><Icon name="arrow-left" />Back to banks</button>
      <h1>Administration</h1>
      <p className="muted">
        Signed in as {learner?.email} ({learner?.role}). Everything here is recorded in the
        audit log against your account.
      </p>

      <div className="btn-row" style={{ marginBottom: '1rem' }}>
        <button onClick={() => void load()} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh queues'}
        </button>
      </div>

      {/* One live region for the outcome of any action on the page, so a
          screen reader hears the result without hunting for it. */}
      <div aria-live="polite">
        {status && <div className="notice">{status}</div>}
      </div>
      {err && <div className="notice notice--warn" role="alert">{err}</div>}

      {/* --------------------------------------------------- password resets */}
      <section>
        <h2>Password resets ({resets.length})</h2>
        <p className="muted">
          This portal sends no email, so a reset is a code you issue and hand over yourself.
          Before you issue one, satisfy yourself that the person asking is the account holder —
          a code is a way into their account, and this screen cannot check that for you.
        </p>
        {resets.length === 0 ? (
          <p className="muted">Nobody is waiting for a reset.</p>
        ) : (
          <ul className="submission-list">
            {resets.map((r) => (
              <li key={r.id} className="card">
                <p style={{ margin: '0 0 0.35rem' }}>
                  <strong>{r.email}</strong>
                  {r.displayName && <span className="muted"> — {r.displayName}</span>}
                </p>
                <dl className="spec">
                  <dt>Asked</dt><dd>{when(r.requestedAt)}</dd>
                  <dt>State</dt>
                  <dd>
                    {r.state === 'issued'
                      ? <>Code issued {when(r.issuedAt)}, expires {when(r.expiresAt)}</>
                      : 'Waiting for a code'}
                  </dd>
                </dl>
                <button type="button" disabled={busy === r.id} onClick={() => void issue(r)}>
                  {r.state === 'issued' ? 'Issue a replacement code' : 'Issue a code'}
                  <span className="visually-hidden"> for {r.email}</span>
                </button>
                {r.state === 'issued' && !issued[r.id] && (
                  <p className="muted" style={{ marginBottom: 0 }}>
                    A code is already live for this request. Issuing a replacement makes the
                    earlier one stop working.
                  </p>
                )}
                {/* Bound to a local so the truthiness check narrows: an index
                    access is re-evaluated as possibly-undefined in the prop. */}
                {((code) => code && <IssuedCodeBox issued={code} email={r.email} />)(issued[r.id])}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ------------------------------------------------- reported questions */}
      <section>
        <h2>Reported answer keys ({disputes.length})</h2>
        <p className="muted">
          Accepting pays the reporter a cookie and records that they were right. It does not
          edit the question: a published bank version is immutable, so the correction ships as
          a new version through the import pipeline.
        </p>
        {disputes.length === 0 ? (
          <p className="muted">No open reports.</p>
        ) : (
          <ul className="submission-list">
            {disputes.map((d) => (
              <li key={d.id} className="card">
                <p style={{ margin: '0 0 0.5rem' }}>{d.stem}</p>
                <dl className="spec">
                  <dt>Question</dt><dd><code>{d.externalId}</code></dd>
                  <dt>Published key</dt><dd>{(d.currentCorrect ?? []).join(', ')}</dd>
                  <dt>Proposed</dt><dd>{(d.proposedResponse ?? []).join(', ')}</dd>
                  <dt>Reported by</dt><dd>{d.raisedBy ?? '—'}</dd>
                  <dt>Raised</dt><dd>{when(d.createdAt)}</dd>
                </dl>
                {d.comment && <p className="muted">“{d.comment}”</p>}
                {d.options?.length > 0 && (
                  <ul className="admin-options">
                    {d.options.map((o) => (
                      <li key={o.key}><span className="choice-key">{o.key}.</span> {o.text}</li>
                    ))}
                  </ul>
                )}
                {noteField(d.id, 'Note to the reporter (required to reject)')}
                <div className="btn-row" style={{ marginTop: '0.6rem' }}>
                  <button type="button" className="btn-primary" disabled={busy === d.id}
                          onClick={() => void resolveDispute(d, true)}>
                    Accept report
                  </button>
                  <button type="button" disabled={busy === d.id}
                          onClick={() => void resolveDispute(d, false)}>
                    Reject report
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ------------------------------------------------ submitted questions */}
      <section>
        <h2>Submitted questions ({contributions.length})</h2>
        <p className="muted">
          Accepting pays the author and marks the question ready for publication. It does not
          publish — that stays with the import pipeline, where validation and the rights
          attestation live.
        </p>
        {contributions.length === 0 ? (
          <p className="muted">Nothing waiting for review.</p>
        ) : (
          <ul className="submission-list">
            {contributions.map((c) => (
              <li key={c.id} className="card">
                <p style={{ margin: '0 0 0.5rem' }}>{c.stem}</p>
                <ul className="admin-options">
                  {c.options.map((o) => (
                    <li key={o.key}>
                      <span className="choice-key">{o.key}.</span> {o.text}
                      {c.correctResponse.includes(o.key) && (
                        <span className="pill pill--ok" style={{ marginLeft: '0.5rem' }}>key</span>
                      )}
                    </li>
                  ))}
                </ul>
                <dl className="spec">
                  <dt>Subject</dt><dd>{c.subject ?? '—'}</dd>
                  <dt>Topic</dt><dd>{c.topicName ?? '—'}</dd>
                  <dt>Author</dt><dd>{c.submittedBy ?? '—'}</dd>
                  <dt>Submitted</dt><dd>{when(c.createdAt)}</dd>
                </dl>
                {c.rationale && <p className="muted">{c.rationale}</p>}
                {noteField(c.id, 'Note to the author (required to reject)')}
                <div className="btn-row" style={{ marginTop: '0.6rem' }}>
                  <button type="button" className="btn-primary" disabled={busy === c.id}
                          onClick={() => void reviewContribution(c, true)}>
                    Accept question
                  </button>
                  <button type="button" disabled={busy === c.id}
                          onClick={() => void reviewContribution(c, false)}>
                    Reject question
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <AdminReading />
    </>
  );
}
